import React, { useEffect, useState } from 'react';
import { Scale, UserPlus, RefreshCw, RotateCcw, Target } from 'lucide-react';
import * as notificationsApi from '../../api/notifications';
import { formatCountdown, formatKickoff } from '../../lib/formatDate';

/**
 * What is waiting on you, on the page you land on.
 *
 * Until this existed every decision lived on the page that owned it - split offers on
 * the game, join requests inside the league - so the only way to find one was to go
 * looking in the right place. A manager discovered requests by opening the league; a
 * split offer could sit unanswered until it lapsed.
 *
 * Deliberately quiet. Nothing waiting means nothing rendered - no empty state, no
 * "you're all caught up" - because this sits above the page someone came to use, and a
 * permanent box that is usually empty trains people to skip the space it occupies. The
 * same goes for a failed read: a landing page should not lead with an error about a
 * sidebar.
 *
 * Every row goes somewhere it can be acted on. None of them act in place: the accept and
 * reject buttons already exist on the league and game pages, complete with the context
 * needed to use them well, and a second set here would be a second thing to keep right.
 *
 * **The soonest outstanding pick is not a row.** It is the top of the page - see
 * {@link PickDeadline}. The server already ranks it first and hands it a deadline of its
 * own, for a reason the old layout threw away: it is the only thing in this list you
 * lose a game by ignoring, and it was rendered at exactly the same weight as a mate
 * asking to join a league. Everything else, second and subsequent picks included, stays
 * a row - a hero per game would be four heroes and no hierarchy at all.
 */
function NotificationsPanel({ onOpenGame, onOpenLeague, onPromoted }) {
  const [actions, setActions] = useState([]);

  /**
   * Which game got promoted, told to whoever is holding the page.
   *
   * The week strip below this one lists every live game, and the promoted one is
   * already the headline - so it needs to know which to leave out. It is reported
   * rather than re-derived, because the ranking that chose it is the server's and a
   * second opinion formed downstream could disagree with it.
   */
  useEffect(() => {
    if (onPromoted) {
      onPromoted(actions.length > 0 && actions[0].type === 'PICK_NEEDED' ? actions[0].gameId : null);
    }
  }, [actions, onPromoted]);

  useEffect(() => {
    let live = true;
    notificationsApi.getPendingActions()
      .then((rows) => {
        if (live) setActions(rows || []);
      })
      .catch(() => {
        // Stay silent - see the note above.
        if (live) setActions([]);
      });
    return () => {
      live = false;
    };
  }, []);

  // The list arrives outstanding-picks-first, soonest deadline first, so the leader is
  // the most urgent pick when there is one at all. Read rather than re-sorted: the
  // ranking is the server's, and a second opinion here could disagree with it.
  const leader = actions.length > 0 && actions[0].type === 'PICK_NEEDED' ? actions[0] : null;
  const rows = leader ? actions.slice(1) : actions;

  if (actions.length === 0) return null;

  return (
    <div className="notifications-panel">
      {leader && (
        <PickDeadline
          action={leader}
          onOpen={() => leader.gameId && onOpenGame && onOpenGame(leader.gameId)}
        />
      )}

      {rows.length > 0 && (
        <>
          <h2 className="notifications-heading">Waiting on you</h2>
          <div className="notifications-list">
            {rows.map((action) => (
              <NotificationRow
                key={rowKey(action)}
                action={action}
                onOpenGame={onOpenGame}
                onOpenLeague={onOpenLeague}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The pick that is about to close, at the size of the thing it costs you.
 *
 * The countdown is `formatCountdown`, the same relative wording the rest of the app
 * uses, because a deadline is only ever read as "have I got time?". The exact time sits
 * under it for the one reader who wants to plan around it rather than answer that
 * question - the two are not alternatives.
 *
 * "Week" rather than "Round" on purpose. Game Progress reframed a week as a round and
 * was right to, but this button leads to the pick screen, which says Week, and a heading
 * that renames the thing on the way to it is the worse of the two inconsistencies.
 */
function PickDeadline({ action, onOpen }) {
  const countdown = formatCountdown(action.deadline);
  const closing = countdown === 'closing now';
  const where = [action.gameName, action.leagueName].filter(Boolean).join(' · ');

  return (
    <div className={`pick-deadline${closing ? ' is-closing' : ''}`}>
      <span className="pick-deadline-icon">
        <Target size={26} />
      </span>

      <div className="pick-deadline-text">
        <span className="pick-deadline-eyebrow">Pick needed</span>
        <h2 className="pick-deadline-title">
          {action.gameWeek ? `Week ${action.gameWeek}` : 'Your pick'}
          {countdown && <> &mdash; {closing ? 'closing now' : countdown}</>}
        </h2>
        <p className="pick-deadline-where">
          {where}
          {action.deadline && (
            <>
              {where && <span aria-hidden="true"> · </span>}
              closes {formatKickoff(action.deadline)}
            </>
          )}
        </p>
      </div>

      <button type="button" className="primary-btn pick-deadline-btn" onClick={onOpen}>
        Pick your team
      </button>
    </div>
  );
}

/**
 * JOIN_REQUESTS has no row id - it stands for a count rather than one request - so the
 * league it belongs to is what makes it unique. PICK_NEEDED has no row id either, and
 * two of them in one league are two different games, so the game has to come before the
 * league here.
 */
function rowKey(action) {
  return `${action.type}-${action.id ?? action.gameId ?? action.leagueId}`;
}

function NotificationRow({ action, onOpenGame, onOpenLeague }) {
  const view = describe(action);
  if (!view) return null;

  const open = () => {
    if (view.target === 'game' && action.gameId && onOpenGame) onOpenGame(action.gameId);
    if (view.target === 'league' && action.leagueId && onOpenLeague) onOpenLeague(action.leagueId);
  };

  const Icon = view.icon;

  return (
    <button className="notification-row" onClick={open}>
      <span className="notification-icon">
        <Icon size={18} />
      </span>
      <span className="notification-text">
        <span className="notification-title">{view.title}</span>
        <span className="notification-detail">{view.detail}</span>
      </span>
      <span className="notification-cta">{view.cta}</span>
    </button>
  );
}

/**
 * Wording lives here rather than in the API, because the same events are already worded
 * on the league and game pages and one of the two copies would drift.
 *
 * An unknown type renders nothing instead of an empty row - a server that grows a new
 * kind of notification should not put a blank button on an old client.
 */
function describe(action) {
  switch (action.type) {
    case 'PICK_NEEDED':
      return {
        icon: Target,
        title: action.gameWeek ? `Week ${action.gameWeek} pick needed` : 'Pick needed',
        // The countdown, not the date: this row is only ever read as "have I got
        // time?", and it is the one row here you lose a game by ignoring.
        detail: [action.gameName || action.leagueName, formatCountdown(action.deadline)]
          .filter(Boolean)
          .join(' — '),
        cta: 'Pick',
        target: 'game',
      };
    case 'SPLIT_OFFER':
      return {
        icon: Scale,
        title: `${action.actorName || 'The other finalist'} wants to split the prize`,
        detail: action.gameName || 'One of your games',
        cta: 'Answer',
        target: 'game',
      };
    case 'JOIN_REQUESTS':
      return {
        icon: UserPlus,
        title:
          action.count === 1
            ? 'Someone wants to join'
            : `${action.count} people want to join`,
        detail: action.leagueName || 'A league you manage',
        cta: 'Review',
        target: 'league',
      };
    case 'ROLLOVER_WINDOW':
      // Nothing else tells a manager their game has rolled over and that, until the next
      // round's deadline, they decide who carries on (backlog item 49) - so this row is there
      // because the window is, whether or not anybody has asked to join yet.
      return {
        icon: RotateCcw,
        title:
          action.count === 0
            ? 'Your game rolled over'
            : action.count === 1
              ? 'Rolled over - someone wants to join'
              : `Rolled over - ${action.count} people want to join`,
        detail: action.deadline
          ? `${action.gameName} · let people in or out until ${formatKickoff(action.deadline)}`
          : `${action.gameName} · let people in or out before the next round starts`,
        cta: 'Review',
        target: 'game',
      };
    case 'RECREATION_NOTICE':
      return {
        icon: RefreshCw,
        title: 'A new game has started',
        detail: action.gameName
          ? `${action.leagueName} — ${action.gameName}`
          : action.leagueName || 'A league you manage',
        cta: 'View',
        target: 'league',
      };
    default:
      return null;
  }
}

export default NotificationsPanel;
